// Hootland Contract Addresses and ABIs
// Base Mainnet / Testnet configuration

const IS_MAINNET = true; // Set to true for Base mainnet

export const BASE_CHAIN_ID_HEX = IS_MAINNET ? '0x2105' : '0x14A34';
export const OWLSDT_DEPLOY_BLOCK = 0;

export const CHAIN_CONFIG = {
  chainId: BASE_CHAIN_ID_HEX,
  name: IS_MAINNET ? 'Base Mainnet' : 'Base Sepolia',
  rpcUrl: IS_MAINNET 
    ? 'https://mainnet.base.org'
    : 'https://sepolia.base.org',
  blockExplorer: IS_MAINNET
    ? 'https://basescan.org'
    : 'https://sepolia.basescan.org'
};

// Contract addresses - UPDATE THESE AFTER DEPLOYMENT
export const CONTRACTS = {
  // Deployed addresses
  HootlandAgent: '0x4e8c7160ac34fc93a4d16dda1817a27455809ba2',
  HootlandWorld: '0xd94d2Af600cE4761f96fdC011B1853Efe68dA42c',
  owlSDT: '0x9ffcf3ce6659baB200AC3f3a292e707Dca4b6c62',
  
  // Legacy (if still needed)
  HootlandMint: '0x143792d046AEfa795Eb3619Ab24F7d3570b5dC1e',
  AgentFactory: '',
  HootlandRegistry: '',
};

// Backwards compatibility
export const ADDRESSES = CONTRACTS;
// BASE_CHAIN_ID_HEX already exported above
// OWLSDT_DEPLOY_BLOCK already exported above

// HootlandAgent Contract ABI (Unified agent contract)
export const HOOTLAND_AGENT_ABI = [
  // Genesis minting
  "function mint(uint256 quantity) payable",
  "function currentPrice() view returns (uint256)",
  "function batchPrice(uint256 quantity) view returns (uint256)",
  "function mintOpen() view returns (bool)",
  "function setMintOpen(bool open)",
  
  // Agent info
  "function genesisSupply() view returns (uint256)",
  "function getAgentInfo(uint256 agentId) view returns (tuple(uint256 agentId, uint8 agentType, uint8 tier, uint256 multiplier, address originalCollection, uint256 originalTokenId, uint256 x, uint256 y, uint256 lastClaimTime, uint256 totalEarned, string metadataURI, bool active))",
  "function getPosition(uint256 agentId) view returns (uint256 x, uint256 y)",
  "function agents(uint256) view returns (uint256 agentId, uint8 agentType, uint8 tier, uint256 multiplier, address originalCollection, uint256 originalTokenId, uint256 x, uint256 y, uint256 lastClaimTime, uint256 totalEarned, string metadataURI, bool active)",
  
  // NFT wrapping
  "function wrapNFT(address collection, uint256 tokenId)",
  "function unwrapNFT(uint256 agentId)",
  "function getAgentByNFT(address collection, uint256 tokenId) view returns (uint256)",
  "function nftToAgent(address, uint256) view returns (uint256)",
  
  // World position
  "function moveAgent(uint256 agentId, uint256 x, uint256 y)",
  "function worldSize() view returns (uint256)",
  
  // Earnings
  "function claimEarnings(uint256 agentId)",
  "function claimEarningsBatch(uint256[] calldata agentIds)",
  "function calculateEarnings(uint256 agentId) view returns (uint256)",
  "function baseEarningRate() view returns (uint256)",
  "function claimCooldown() view returns (uint256)",
  
  // Metadata & IPFS
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function setTierImageCID(uint8 tier, string calldata cid)",
  "function tierImageCIDs(uint8) view returns (string)",
  "function setBaseURI(string calldata uri)",
  
  // ERC721 standard
  "function balanceOf(address owner) view returns (uint256)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
  "function totalSupply() view returns (uint256)",
  "function approve(address to, uint256 tokenId)",
  "function getApproved(uint256 tokenId) view returns (address)",
  "function setApprovalForAll(address operator, bool approved)",
  "function isApprovedForAll(address owner, address operator) view returns (bool)",

  // ERC721 Events
  "event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)",
  "function transferFrom(address from, address to, uint256 tokenId)",
  "function safeTransferFrom(address from, address to, uint256 tokenId)",
  
  // Admin
  "function owner() view returns (address)",
  "function withdraw()",
  
  // Events
  "event GenesisMinted(uint256 indexed agentId, address indexed owner, uint8 tier, uint256 multiplier, uint256 x, uint256 y)",
  "event NFTWrapped(uint256 indexed agentId, address indexed owner, address collection, uint256 tokenId, uint256 x, uint256 y)",
  "event NFTUnwrapped(uint256 indexed agentId, address indexed owner, address collection, uint256 tokenId)",
  "event AgentMoved(uint256 indexed agentId, uint256 x, uint256 y)",
  "event EarningsClaimed(uint256 indexed agentId, address indexed owner, uint256 amount)"
];

// HootlandWorld Contract ABI
export const HOOTLAND_WORLD_ABI = [
  "function agentContract() view returns (address)",
  "function worldSize() view returns (uint256)",
  "function totalResources() view returns (uint256)",
  
  // Resources
  "function spawnResource(uint8 resourceType, uint256 x, uint256 y, uint256 amount, uint256 respawnTime)",
  "function spawnResourceBatch(uint8[] calldata types, uint256[] calldata xs, uint256[] calldata ys, uint256[] calldata amounts)",
  "function resources(uint256) view returns (uint256 id, uint8 resourceType, uint256 x, uint256 y, uint256 amount, uint256 respawnTime, bool active)",
  "function worldMap(uint256, uint256) view returns (uint256)",
  "function hasResource(uint256 x, uint256 y) view returns (bool)",
  "function getResourceAt(uint256 x, uint256 y) view returns (tuple(uint256 id, uint8 resourceType, uint256 x, uint256 y, uint256 amount, uint256 respawnTime, bool active))",
  "function getResourcesInRange(uint256 agentId, uint256 range) view returns (tuple(uint256 id, uint8 resourceType, uint256 x, uint256 y, uint256 amount, uint256 respawnTime, bool active)[])",
  
  // Gathering
  "function gatherResource(uint256 agentId, uint256 resourceId)",
  "function gatherCooldown() view returns (uint256)",
  "function lastGatherTime(uint256 agentId) view returns (uint256)",
  "function resourceBonuses(uint8) view returns (uint256)",
  
  // Biomes
  "function createBiome(string calldata name, uint256 resourceMultiplier, uint256 x, uint256 y, uint256 width, uint256 height)",
  "function biomes(uint256) view returns (uint256 id, string name, uint256 resourceMultiplier, uint256 x, uint256 y, uint256 width, uint256 height)",
  "function getBiomeAt(uint256 x, uint256 y) view returns (tuple(uint256 id, string name, uint256 resourceMultiplier, uint256 x, uint256 y, uint256 width, uint256 height))",
  
  // Events
  "event ResourceSpawned(uint256 indexed resourceId, uint8 resourceType, uint256 x, uint256 y, uint256 amount)",
  "event ResourceGathered(uint256 indexed resourceId, uint256 indexed agentId, address indexed owner, uint8 resourceType, uint256 amount, uint256 bonus)",
  "event ResourceDepleted(uint256 indexed resourceId)",
  "event ResourceRespawned(uint256 indexed resourceId)",
  "event BiomeCreated(uint256 indexed biomeId, string name)"
];

// owlSDT Token ABI
export const OWL_SDT_ABI = [
  "function name() view returns (string)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function totalSupply() view returns (uint256)",
  "function balanceOf(address account) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function transferFrom(address from, address to, uint256 amount) returns (bool)",
  "function mint(address to, uint256 amount)",
  "function addMinter(address minter)",
  "function removeMinter(address minter)",
  "function isMinter(address) view returns (bool)",
  "event Transfer(address indexed from, address indexed to, uint256 value)",
  "event Approval(address indexed owner, address indexed spender, uint256 value)"
];

// ERC721 ABI (for external NFTs)
export const ERC721_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenURI(uint256 tokenId) view returns (string)",
  "function approve(address to, uint256 tokenId)",
  "function getApproved(uint256 tokenId) view returns (address)",
  "function setApprovalForAll(address operator, bool approved)",
  "function isApprovedForAll(address owner, address operator) view returns (bool)",
  "function transferFrom(address from, address to, uint256 tokenId)",
  "function safeTransferFrom(address from, address to, uint256 tokenId, bytes calldata data)",
  "function safeTransferFrom(address from, address to, uint256 tokenId)"
];

// Legacy ABIs (for backwards compatibility)
export const AGENT_FACTORY_ABI = HOOTLAND_AGENT_ABI;
export const REGISTRY_ABI = [];

// IPFS Gateway URLs
export const IPFS_GATEWAYS = [
  'https://gateway.pinata.cloud/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://dweb.link/ipfs/'
];

// Helper to convert IPFS URI to HTTP URL
export function ipfsToHttp(ipfsUri, gatewayIndex = 0) {
  if (!ipfsUri) return '';
  if (ipfsUri.startsWith('ipfs://')) {
    const cid = ipfsUri.replace('ipfs://', '');
    return IPFS_GATEWAYS[gatewayIndex] + cid;
  }
  return ipfsUri;
}

// Helper to fetch metadata from IPFS
export async function fetchMetadata(ipfsUri, gatewayIndex = 0) {
  try {
    const url = ipfsToHttp(ipfsUri, gatewayIndex);
    const response = await fetch(url);
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch metadata:', error);
    return null;
  }
}

// Tier configuration
export const TIER_CONFIG = {
  0: { name: 'Agent', color: '#4A90D9', multiplier: 1.2, earnings: 12, rarity: 'Common' },
  1: { name: 'Sentinel', color: '#9B59B6', multiplier: 1.5, earnings: 15, rarity: 'Uncommon' },
  2: { name: 'Overseer', color: '#E74C3C', multiplier: 2.0, earnings: 20, rarity: 'Rare' },
  3: { name: 'Elder', color: '#F39C12', multiplier: 3.0, earnings: 30, rarity: 'Legendary' }
};

// Resource types
export const RESOURCE_TYPES = {
  0: { name: 'None', icon: '❌', color: '#888' },
  1: { name: 'Wood', icon: '🪵', color: '#8B4513' },
  2: { name: 'Stone', icon: '🪨', color: '#808080' },
  3: { name: 'Gold', icon: '🪙', color: '#FFD700' },
  4: { name: 'Food', icon: '🍎', color: '#FF6347' },
  5: { name: 'Crystal', icon: '💎', color: '#00CED1' }
};

// Pricing tiers
export const PRICING_TIERS = [
  { threshold: 0, price: 0.0001, name: 'Early Bird' },
  { threshold: 1000, price: 0.001, name: 'Growth' },
  { threshold: 10000, price: 0.01, name: 'Main' },
  { threshold: 50000, price: 0.05, name: 'Premium' }
];
